"use client";

import Link from "next/link";
import { useEffect, useId, useRef, useState } from "react";
import { addLineItem, openCartDrawer } from "../../lib/cart-store";
import { wixSized } from "../../lib/media";
import type { CatalogLite } from "../../lib/types";

/**
 * Inline quick-add panel — the Glaza God pattern. Opens in place beneath the
 * product's row instead of throwing a modal over the grid, so the shopper never
 * loses their place in the collection.
 *
 * It renders entirely from the lightweight catalog row: `options` now travels
 * on CatalogLite, so choosing a size costs zero network. The old Quick View
 * modal fetched /api/product on every open, which both added a round trip to
 * the first interaction and put ordinary grid browsing behind the one
 * Cloudflare Free-plan WAF rate-limit rule — it covers /api/ and trips at
 * roughly ten requests in ten seconds, i.e. ten quick views.
 */
export default function QuickAddPanel({
  product,
  onClose,
}: {
  product: CatalogLite;
  onClose: () => void;
}) {
  const options = product.options ?? [];
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const uid = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLParagraphElement>(null);

  // onClose is an inline arrow in every parent; keep it in a ref so the mount
  // effect (focus + scroll + Esc) runs once instead of on every parent render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  // The grid button that opened the panel; focus goes back to it on close.
  const openerRef = useRef<HTMLElement | null>(null);

  function triggerEl(): HTMLElement | null {
    const opener = openerRef.current;
    if (opener?.isConnected) return opener;
    return document.querySelector<HTMLElement>(`[data-quick-add-trigger="${CSS.escape(product.id)}"]`);
  }

  function closePanel() {
    triggerEl()?.focus({ preventScroll: true });
    onCloseRef.current();
  }

  useEffect(() => {
    const active = document.activeElement;
    openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
    headingRef.current?.focus({ preventScroll: true });
    panelRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      // Only when focus is in the panel (or nowhere in particular / on the
      // opening button) — not an Esc aimed at the mega menu, cart drawer, etc.
      const a = document.activeElement;
      const inside = panelRef.current?.contains(a) ?? false;
      const neutral = !a || a === document.body || a === openerRef.current;
      if (!inside && !neutral) return;
      closePanel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allChosen = options.every((o) => selected[o.name]);

  async function add() {
    if (!allChosen) {
      setError(`Please choose a ${options.find((o) => !selected[o.name])?.name.toLowerCase()}.`);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await addLineItem({
        productId: product.id,
        choices: product.manageVariants ? undefined : selected,
        quantity: 1,
      });
      // Park focus on the grid button first so the cart drawer remembers a
      // live element to hand focus back to (this panel is about to unmount).
      triggerEl()?.focus({ preventScroll: true });
      onCloseRef.current();
      openCartDrawer();
    } catch (err) {
      console.error("[quick-add] failed", err);
      setError("Could not add to cart — please try again.");
      setBusy(false);
    }
  }

  return (
    <div
      ref={panelRef}
      className="col-span-full -mt-4 border border-ink/15 bg-panel"
      role="region"
      aria-label={`Quick add ${product.name}`}
    >
      <div className="flex flex-col gap-6 p-6 sm:flex-row sm:items-center sm:gap-10 sm:p-7">
        <div className="flex min-w-0 flex-1 items-center gap-5">
          <div className="hidden h-28 w-24 shrink-0 items-center justify-center bg-mist/40 sm:flex">
            {product.image ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={wixSized(product.image, 192, 224)} alt="" className="h-full w-full object-cover" />
            ) : (
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.2"
                className="text-ink/20"
                aria-hidden="true"
              >
                <rect x="3" y="3" width="18" height="18" rx="1.5" />
                <circle cx="8.5" cy="8.5" r="1.6" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            )}
          </div>
          <div className="min-w-0">
            <p
              ref={headingRef}
              tabIndex={-1}
              className="text-[13px] uppercase tracking-[0.12em] outline-none"
            >
              {product.name}
            </p>
            <p className="mt-1 flex items-center gap-2 text-[13px] text-ink/70">
              {product.compareAt && <span className="text-ink/40 line-through">{product.compareAt}</span>}
              {product.price}
            </p>
            <Link
              href={`/shop/${product.slug}`}
              className="mt-3 inline-block text-[11px] uppercase tracking-[0.15em] text-ink/60 underline transition hover:text-ink"
            >
              View full details →
            </Link>
          </div>
        </div>

        <div className="flex w-full flex-col gap-4 sm:max-w-sm">
          {options.map((opt) => (
            <div key={opt.name}>
              <p id={`${uid}-${opt.name}`} className="eyebrow mb-2 text-ink/60">
                {opt.name}
              </p>
              <div role="group" aria-labelledby={`${uid}-${opt.name}`} className="flex flex-wrap gap-2">
                {opt.choices.map((c) => {
                  const active = selected[opt.name] === c;
                  return (
                    <button
                      key={c}
                      type="button"
                      aria-pressed={active}
                      onClick={() => {
                        setError(null);
                        setSelected((s) => ({ ...s, [opt.name]: c }));
                      }}
                      className={`min-w-11 border px-4 py-2 text-[12px] uppercase tracking-wide transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink ${
                        active ? "border-ink bg-ink text-paper" : "border-ink/30 hover:border-ink"
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={add}
              disabled={busy || !product.inStock}
              className="flex-1 bg-ink px-8 py-3.5 text-[12px] uppercase tracking-[0.18em] text-paper transition hover:bg-gold-deep disabled:cursor-not-allowed disabled:opacity-40"
            >
              {!product.inStock ? "Sold Out" : busy ? "Adding…" : "Add to Cart"}
            </button>
            <button
              type="button"
              onClick={closePanel}
              aria-label={`Close quick add for ${product.name}`}
              className="border border-ink/25 px-4 py-3.5 text-[12px] uppercase tracking-[0.18em] transition hover:border-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
            >
              Close
            </button>
          </div>

          {/* role=alert lives on a stable node so the message is announced on insert. */}
          {error && (
            <p role="alert" className="text-[12px] text-red-700">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
